import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Link2, Loader2, Upload, X } from "lucide-react";
import { adminApi } from "../api";
import { resolveImage } from "../../lib/data";
import { toast } from "sonner";

export default function ImageUploader({ value, onChange, testId = "image-uploader" }) {
  const [mode, setMode] = useState(value?.startsWith("/api/") ? "upload" : "url");
  const [url, setUrl] = useState(value || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setUrl(value || "");
  }, [value]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await adminApi.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      setUrl(res.data.url);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error("Upload fallito", {
        description: err.response?.data?.detail || "Riprova",
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid={testId} className="space-y-3">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode("url")}
          className={`px-3 py-1.5 rounded-full text-xs border ${
            mode === "url"
              ? "bg-white text-black border-white"
              : "border-white/15 text-white/60 hover:text-white"
          }`}
        >
          <Link2 size={12} className="inline mr-1" /> URL esterno
        </button>
        <button
          type="button"
          onClick={() => setMode("upload")}
          className={`px-3 py-1.5 rounded-full text-xs border ${
            mode === "upload"
              ? "bg-white text-black border-white"
              : "border-white/15 text-white/60 hover:text-white"
          }`}
        >
          <Upload size={12} className="inline mr-1" /> Carica file
        </button>
      </div>

      {mode === "url" ? (
        <input
          type="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            onChange(e.target.value);
          }}
          placeholder="https://…"
          data-testid={`${testId}-url-input`}
          className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-2 text-sm"
        />
      ) : (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            data-testid={`${testId}-file-input`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs hover:bg-white/5"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? "Caricamento…" : "Scegli immagine"}
          </button>
          <p className="mt-2 text-[11px] text-white/40">
            JPG, PNG, WebP (max 10MB).
          </p>
        </div>
      )}

      {url && (
        <div className="relative inline-block mt-2">
          <img
            src={resolveImage(url)}
            alt="Anteprima"
            className="h-28 rounded-md border border-white/10 object-cover"
            onError={(e) => {
              e.currentTarget.style.opacity = "0.4";
            }}
          />
          <button
            type="button"
            onClick={() => {
              setUrl("");
              onChange("");
            }}
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white grid place-items-center"
            aria-label="Rimuovi"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {!url && (
        <div className="h-28 w-40 rounded-md border border-dashed border-white/15 grid place-items-center text-white/30">
          <ImageIcon size={20} />
        </div>
      )}
    </div>
  );
}
